import React, { useState, useEffect } from 'react'
import styles from './MicrositeImageUploader.module.css'
import { Image as ImageIcon, X, AlertCircle, Check } from 'lucide-react'

/**
 * MicrositeImageUploader
 *
 * URL-based image field with a live preview thumbnail. Lokaci Pro's
 * media system handles actual file storage elsewhere — this component
 * is the "paste a URL, see a preview" pattern used for logos, favicons,
 * OG images, cover images etc. across the microsite module.
 *
 * Usage:
 *   <MicrositeImageUploader
 *     label="Logo URL"
 *     value={form.logoUrl}
 *     onChange={(v) => updateField('logoUrl', v)}
 *     placeholder="https://example.com/logo.png"
 *   />
 */
const MicrositeImageUploader = ({
    label,
    value,
    onChange,
    placeholder = 'https://example.com/image.jpg',
    helperText,
    aspectHint, // e.g. "Recommended 1200x630px"
    disabled = false
}) => {
    const [imgStatus, setImgStatus] = useState('idle') // idle | loading | ok | error

    useEffect(() => {
        if (!value) {
            setImgStatus('idle')
            return
        }
        setImgStatus('loading')
    }, [value])

    const handleClear = () => {
        onChange?.('')
    }

    return (
        <div className={styles.field}>
            {label && <label className={styles.label}>{label}</label>}

            <div className={styles.row}>
                <div className={styles.thumbWrap}>
                    {value ? (
                        <>
                            <img
                                src={value}
                                alt={label || 'Preview'}
                                className={styles.thumb}
                                onLoad={() => setImgStatus('ok')}
                                onError={() => setImgStatus('error')}
                                style={{ display: imgStatus === 'error' ? 'none' : 'block' }}
                            />
                            {imgStatus === 'error' && (
                                <div className={styles.thumbError}>
                                    <AlertCircle size={16} />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={styles.thumbEmpty}>
                            <ImageIcon size={16} />
                        </div>
                    )}
                </div>

                <div className={styles.inputCol}>
                    <input
                        type="text"
                        className={styles.input}
                        value={value ?? ''}
                        onChange={(e) => onChange?.(e.target.value)}
                        placeholder={placeholder}
                        disabled={disabled}
                    />
                    <div className={styles.metaRow}>
                        {imgStatus === 'ok' && (
                            <span className={styles.statusOk}>
                                <Check size={11} />
                                Image loads correctly
                            </span>
                        )}
                        {imgStatus === 'error' && (
                            <span className={styles.statusError}>
                                <AlertCircle size={11} />
                                Couldn't load this image — check the URL
                            </span>
                        )}
                        {imgStatus === 'idle' && (aspectHint || helperText) && (
                            <span className={styles.hint}>{helperText || aspectHint}</span>
                        )}
                    </div>
                </div>

                {value && (
                    <button
                        type="button"
                        className={styles.clearBtn}
                        onClick={handleClear}
                        title="Remove image"
                        disabled={disabled}
                    >
                        <X size={13} />
                    </button>
                )}
            </div>
        </div>
    )
}

export default MicrositeImageUploader